import { PBX_REGIONS, regions, divisions, PBX_DIVISION_KEYS, PBX_DIVISIONS ,pbxGlobalSelect,PBX_GS } from '../helper/constants';
import { mapState, mapMutations, mapGetters } from 'vuex';
import _, { set as _set, find, get } from 'lodash';
import { contactUsPath, PlanDisabledStatusEnum, planTypes } from '@/common/constants';
import {
  computedPbxPlans,
  computedPbxCallingPlans,
  computedPbxTollFreePlans,
  computedPbxPhoneNumberPlans,
  computedPbxAddonPlans,
  computedPbxMeteredPlans,
  computedPbxUnlimitedPlans,
  computedPbxAdditionalPlans,
  PBX_SUB_TYPES,
} from '@/common/service/computers/plans/pbx';
import { getCartInfo } from '@/common/service/default-mapper';
import { BILLING_CYCLE } from '@/common/constants';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import FullComparisonDialog from '@/plans/ZOOM_PHONE/component/dialog/full-comparison-dialog.vue';
import { getPbxContactSalesLink } from '@/utils/i18n-shortcuts';
import { getQueryByLocationSearch } from '@/utils/index';

export default {
  inject: ['$core'],
  data() {
    return {
      regions,
      divisions,
      activeRegionKey: regions[0].key,
      regionsConfig: this.fillSelectModel(),
      maxLimitedPlansCount: 99,
      pbxContactSalesLink: getPbxContactSalesLink()
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getSubscriptions', 'plancodeMapSubscriptionDisabled', 'getSelectedPlansMap', 'basicConfig', 'boughtPlans']),
    ...mapState({ planMapFlat: (state) => state.ShoppingCart.planMapFlat }),
    regionalPlans() {
      const currentRegionPlan = computedPbxPlans(this.planMapFlat).filter((x) => x.region === this.activeRegionKey);
      // remove JP Unlimited
      return window.newBillingConfig?.hideZoomPhoneUnlimitedJP ? currentRegionPlan.filter((x) => !(x.region === PBX_REGIONS.JP && x.sub_type === PBX_SUB_TYPES.UNLIMITED)) : currentRegionPlan;
    },
    divisionPlans() {
      return computedPbxPlans(this.planMapFlat).filter((x) => {
        return x.division === this.activeRegionKey}
    );
    },
    regionalCallingPlans() {
      return computedPbxCallingPlans(this.regionalPlans);
    },
    divisionCallingPlans() {
      return computedPbxCallingPlans(this.divisionPlans);
    },
    globalSelectPlans(){
      return computedPbxPlans(this.planMapFlat).filter(item => PBX_GS.includes(item.category))
    },
    globalSelectCallingPlans(){
      return  computedPbxCallingPlans(this.globalSelectPlans);
    },
    globalSelectAdditionalPlans(){
      return [...computedPbxPhoneNumberPlans(this.globalSelectPlans), ...computedPbxTollFreePlans(this.globalSelectPlans)];
    },
    regionalAdditionalPlans() {
      return [...computedPbxPhoneNumberPlans(this.regionalPlans), ...computedPbxTollFreePlans(this.regionalPlans)];
    },
    regionsConfigJson() {
      return JSON.stringify(this.regionsConfig);
    },
    hasBoughtActiveCallingPlan() {
      return this.boughtPlans.some(p => ([
        planTypes.ZOOM_ONE_BUNDLE,
        planTypes.ZOOM_UNITED,
        planTypes.ZONE_PRO_PLUS,
        planTypes.ZONE_EDU_PLUS,
        // planTypes.ZOOM_PHONE
      ].includes(p.type)) && !p.cartInfo?.cancelled);
    },
    callingPlanDeletable() {
      // Check if the calling plan is deletable
      let count = 0
      Object.keys(this.regionsConfig).forEach((key) => {
        // check if the calling plan is disabled
        const callings = this.regionsConfig[key].callings.filter(p => !this.plancodeMapSubscriptionDisabled[p.id]);
        count += callings.length
      })
      return count > 1 || this.hasBoughtActiveCallingPlan;
    },
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    selectedCallingPlans() {
      return computedPbxCallingPlans(this.getSelectedPlans);
    },
    selectedPbxAddonPlans() {
      return computedPbxAddonPlans(this.getSelectedPlans);
    },
    selectedAdditionalPlans() {
      return computedPbxAdditionalPlans(this.getSelectedPlans);
    },
    selectedPlansLabelList() {
      return [...this.selectedCallingPlans, ...this.selectedAdditionalPlans].map(plan => {
        const regionLabel = this.$t(this.regions.find(region => region.key === plan.region)?.label)
        const divisionLabel = plan?.division? this.$t(this.divisions.find(division => division?.key === plan?.division)?.label) : ''
        const globalLabel = PBX_GS.includes(plan.category)?this.$t('billing.pbx.global_select'):''
        const planName = this.$t(plan.planNameKey)
        const numbersLabel = `${plan.cartInfo.selected_number} ${this.$t('billing.planform.unit.number').toLowerCase()}`
        const price = `${this.$money(plan.amount)}${this.getTranslateUnit(plan.billing_cycle)}`
        return `${regionLabel || divisionLabel || globalLabel} • ${planName} • ${numbersLabel} • ${price}`
      });
    },
    totalLimitedPlansCount() {
      return [...this.selectedCallingPlans, ...this.selectedPbxAddonPlans]
        .reduce((total, x) => total + (x.cartInfo.selected_number ?? 0), 0)
    },
    dataTrackingCallingPlan() {
      return {
        buyInteraction: 'zoom phone calling plan',
        buySelection: this.getRegionLabelById(this.activeRegionKey)
      }
    },
    dataTrackingNumbers() {
      return {
        buyInteraction: 'zoom phone additional numbers',
        buySelection: this.getRegionLabelById(this.activeRegionKey)
      }
    },
    boughtJPPbxPlans() {
      return this?.boughtPbxPlans?.filter?.(item => item?.region === PBX_REGIONS.JP)
    },
    boughtProPlans() {
      return this?.boughtPbxPlans?.filter?.(item => item?.division === PBX_DIVISIONS.PRO)
    },
    boughtCAPlans() {
      return this?.boughtPbxPlans?.filter?.(item => item?.division === PBX_DIVISIONS.CA)
    },
    boughtGSPlans() {
      return this?.boughtPbxPlans?.filter?.(item => PBX_GS.includes(item.category))
    },
    hasBoughtJPPbxPlans() {
      return !!this.boughtJPPbxPlans?.length;
    },
    hasBoughtPro() {
      return !!this.boughtProPlans?.length;
    },
    hasBoughtCA() {
      return !!this.boughtCAPlans?.length;
    },
    hasBoughtGS() {
      return !!this.boughtGSPlans?.length;
    },
    isProCA(){
      return PBX_DIVISION_KEYS.includes(this.activeRegionKey)
    },
    isGS(){
      return this.activeRegionKey === 'GS'
    },
    disableDownSell() {
      return this.basicConfig.downSellData.disableDownSell;
    },
    regionArea(){
      let regionArea = regions.concat(divisions)
      if(this.$context.is.edit || this.$context.is.add2cart){
        regionArea = regions.concat(divisions).concat(pbxGlobalSelect)
      }
      return regionArea
    },
    regionsGSList() {
      if(this.hasBoughtGS) return  [ { key: 'GS', label: 'billing.pbx.global_select', desc: 'billing.pbx.calling_plans_Global_Select' }];
      if(!this.$config('enableZoomPhoneGlobalSelect') || !this.enableGlobal) return []
      return [ { key: 'GS', label: 'billing.pbx.global_select', desc: 'billing.pbx.calling_plans_Global_Select' }];
    },
    enableJPBuy() {
      return this.$config('enableZPJapanOnlineBuy');
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlans', 'removePlan']),
    mapSelectOptions(plans_, filterBillingCycle = '') {
      let plans = plans_;
      if (filterBillingCycle) plans = plans.filter(x => x.billing_cycle === filterBillingCycle);
      const isSingleSubType = sub_type => plans.filter(x => x.sub_type === sub_type).length == 1;
      const isDirectSales = this.basicConfig?.isDirectSales
      return plans.map((x) => {
        const hasBought = this.boughtPlans.find((bp) => x.plan_code === bp.plan_code);
        const isGSPN = this.isGS && x.sub_type === 'PN'
        let res = {
          id: x.plan_code,
          name: isSingleSubType(x.sub_type) ? (isGSPN ? this.$t('billing.pbx.plan_name.ZSD') : this.$t(`billing.pbx.sub_types.${x.sub_type}`)) : this.$t(x.planNameKey),
          desc: `${this.$money(x.amount, { forceDiff: true })}${this.getTranslateUnit(x.billing_cycle)}`,
          tooltip: this.getPlanTips(x.plan_code),
          disabled: this.plancodeMapSubscriptionDisabled[x.plan_code] ?? false,
          inputTooltip: this.plancodeMapSubscriptionDisabled[x.plan_code] === PlanDisabledStatusEnum['CANCELED'] ? this.$t('billing.pbx.select_disabled_tips') : '',
          min: hasBought?.isStandardPrice === false ? hasBought.cartInfo.selected_number : 1, // TODO
          max: 9999,
        };
        if ((isDirectSales && hasBought) || hasBought?.isDisablePlanChangeOnline || hasBought?.ratePlans?.length > 1) {
          Object.assign(res, {
            disableSwitch: true,
            disableDelete: true,
            deleteTips: this.$t('billing.cancel_disable_contact_us', {
              url: contactUsPath,
            }),
          });
        }
        if (this.disableDownSell && hasBought) {
          Object.assign(res, {
            min: hasBought.cartInfo.selected_number,
            decreaseTips: this.$t('billing.error.can_not_bought_plan_tips'),
          })
        }
        return res
      });
    },
    getTranslateUnit(billingCycle) {
      switch (billingCycle) {
        case BILLING_CYCLE.MONTHLY:
          return `/${this.$t('common.month')}`;
        case BILLING_CYCLE.ANNUAL:
          return `/${this.$t('common.year')}`;
        case BILLING_CYCLE.QUARTERLY:
          return this.$t('billing.common.per_billing_cycle.quarterly');
        case BILLING_CYCLE.TWO_YEARS:
          return this.$t('billing.common.per_billing_cycle.two_years');
        case BILLING_CYCLE.THREE_YEARS:
          return this.$t('billing.common.per_billing_cycle.three_years');
      }
    },
    getRegionLabelById(key) {
      let val = get(this.regions?.find(r => {
        return r.key === key;
      }), 'label', '');
      if( key === 'GS') return this.$t('billing.pbx.global_select')
      if (val) {
        return this.$t(val);
      }
      return '';
    },
    getPlanTips(planCode) {
      const tollfreePlans = computedPbxTollFreePlans(this.planMapFlat)
      const targetTollfreePlan = tollfreePlans.find(x => x.plan_code === planCode)
      const isGSTF = targetTollfreePlan?.category === 'ZGT'
      if (targetTollfreePlan) {
        if(isGSTF) return this.$t('billing.pbx.GS_tollfree_tips')
        return this.$t(`billing.pbx.${targetTollfreePlan.category === 'ZNT' ? 'NZ' : targetTollfreePlan.region}_tollfree_tips`)
      }

      const meteredPlans = computedPbxMeteredPlans(this.planMapFlat)
      const targetMeteredPlan = meteredPlans.find(x => x.plan_code === planCode)
      if (targetMeteredPlan) {
        return this.$t('billing.pbx.calling_plans_metered_tips')
      }

      return ''
    },
    beforeChangeCallingPlan(newSelectList, oldSelectList, done) {
      const selectedPbxUnlimitedPlans = computedPbxUnlimitedPlans(this.selectedCallingPlans)
      // pro plus + biz plus + uc bundle
      const boughtPbxUnlimitedPlans = this.boughtPlans.filter(p => [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZOOM_UNITED, planTypes.ZONE_PRO_PLUS].includes(p.type));
      if (selectedPbxUnlimitedPlans.length === 1 &&
          boughtPbxUnlimitedPlans.length === 0 &&
          this.selectedPbxAddonPlans.length > 0 &&
          newSelectList.length <= oldSelectList.length && // Exclude the case of addition.
          oldSelectList.some(x => find(selectedPbxUnlimitedPlans, { plan_code: x.id })) &&
          newSelectList.every(x => !find(selectedPbxUnlimitedPlans, { plan_code: x.id }))
      ) {
        this.$confirm(
          this.$t('billing.pbx.auto_remove_unlimited_addons_info') || '',
          this.$t('billing.pbx.auto_remove_unlimited_addons_title') || '',
          {
            cancelButtonText: this.$t('billing.consumption_cancel.go_back'),
            confirmButtonText: this.$t('billing.pbx.proceed'),
          },
        ).then(() => {
          done(newSelectList)
          this.$core.removePlanByCode(planTypes.map([planTypes.ZOOM_PHONE$ZPI])[0], this.selectedPbxAddonPlans[0]);
        }).catch(() => {
          done(oldSelectList)
        });
      } else {
        done(newSelectList)
      }
    },
    fillSelectModel(thisRegionsConfig = {}) {
      // ignoring thisRegionsConfig will return a plain object to init data.regionsConfig
      const _map = (plans) =>
        plans.map((x) => ({
          id: x.plan_code,
          number: x.cartInfo?.selected_number ?? 0,
        }));
      let regionArea = regions.concat(divisions)
      if(this.$context.is.edit || this.$context.is.add2cart){
        regionArea = regions.concat(divisions).concat(pbxGlobalSelect)
      }
      regionArea.forEach((region) => {
        const plans = _.chain(this.boughtPbxPlans || [])
          .map((p) => (this.getSelectedPlansMap[p.plan_code] || p)) // selected plans overwrite bought plans
          .concat(this.getSelectedPlans || [])
          .filter((x) => x.region === region.key
          || (PBX_DIVISION_KEYS.includes(region?.key) && x.category === region.category)
          || (region?.key === 'GS' && PBX_GS.includes(x.category)))
          .uniqBy((p) => p.plan_code)
          .value();

        const callingPlans = computedPbxCallingPlans(plans);
        const additionalPlans = [...computedPbxPhoneNumberPlans(plans), ...computedPbxTollFreePlans(plans)];
        _set(thisRegionsConfig, `${region.key}.callings`, _map(callingPlans));
        _set(thisRegionsConfig, `${region.key}.additions`, _map(additionalPlans));
      });
      return thisRegionsConfig;
    },
    testRegionalCallingPlansFull(billingCycle) {
      const plans = (this.isGS ? this.globalSelectCallingPlans :this.regionalCallingPlans).filter(x => x.billing_cycle === billingCycle);
      return this.regionsConfig[this.activeRegionKey]?.callings?.length === plans.length;
    },
    testRegionalAdditionalPlansFull(billingCycle) {
      const plans =  (this.isGS ? this.globalSelectAdditionalPlans :this.regionalAdditionalPlans).filter(x => x.billing_cycle === billingCycle);
      return this.regionsConfig[this.activeRegionKey]?.additions?.length === plans.length;
    },
    async showPlanDetailDialog() {
      this.$tracking.buyInteraction('plan details selected', 'zoom phone');
      await this.fullComparisonDialog.confirm({ showAction: false });
    },
    $init() {
      // to be implemented
      this.fullComparisonDialog = new ServiceConfirm(FullComparisonDialog, this.$store);
    },
    showCheckClass(region) {
      return this.regionsConfig[region.key].callings.length || this.regionsConfig[region.key].additions.length ? 'opc-tag-radio__option--checked' : '';
    },
    getCallingPlanLabel(region) {
      if(this.showCheckClass(region)) {
        // Ax Issue: ZOOM-535354 Issue 4
        return this.$t(region.label) + '<div style="visibility: hidden;width:0;height:0;">include</div>';
      }
      return this.$t(region.label);
    },
  },
  watch: {
    regionsConfigJson() {
      // watch regionsConfig change and update shopping cart
      let regionArea = this.isGlobalSelect? pbxGlobalSelect : regions.concat(divisions)
      if(this.$context.is.edit || this.$context.is.add2cart){
        regionArea = regions.concat(divisions).concat(pbxGlobalSelect)
      }
      const modelList = regionArea.reduce((acc, region) => {
        const { callings = [], additions = [] } = this.regionsConfig[region.key];
        acc = [...acc, ...callings, ...additions];
        return acc;
      }, []);

      // reset these pbx plans in shopping cart
      const resetPbxPlans = [
        ...computedPbxCallingPlans(this.getSelectedPlans),
        ...computedPbxTollFreePlans(this.getSelectedPlans),
        ...computedPbxPhoneNumberPlans(this.getSelectedPlans)
      ].map(x => ({
        plan_code: x.plan_code,
        cartInfo: getCartInfo(),
      }));

      this.updateSelectPlans([
        ...resetPbxPlans,
        ...modelList.map(x => ({
          plan_code: x.id,
          cartInfo: { selected_number: x.number || 0 }
        }))
      ]);
    },
  },
  created() {
    this.$init();
  },
  mounted() {
    if (this.$config('hideZoomPhoneUKIRPlans')) {
      this.regions = this.regions.filter(e => e.key !== PBX_REGIONS.UK_AND_IRELAND);
    }
    if ((!this.hasBoughtJPPbxPlans && !this.enableJPBuy) || this.$config('hideZoomPhoneJPPlans')) {
       this.regions = this.regions.filter(e => e.key !== PBX_REGIONS.JP);
    }
    const hideZPProCA = this.$config('hideZPProCA')
    if (!this.hasBoughtPro || hideZPProCA) {
      this.divisions = this.divisions.filter(e => e.key !== PBX_DIVISIONS.PRO);
    }
    if (!this.hasBoughtCA || hideZPProCA) {
      this.divisions = this.divisions.filter(e => e.key !== PBX_DIVISIONS.CA);
    }

    const { region: defaultRegionKey, division: defaultDivision } = getQueryByLocationSearch()
    const regionList = [...this.regions, ...this.regionsGSList]
    if (regionList.some(r => r.key === defaultRegionKey)) {
      this.activeRegionKey = defaultRegionKey
    }

    if (this.divisions.some(r => r.key === defaultDivision)) {
      this.activeRegionKey = defaultDivision
    }
  }
};
